<template>
  <div :class="themeClasses.pageContainer" class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div :class="themeClasses.card" class="card">
          <div :class="themeClasses.cardHeader" class="card-header">
            <h3 :class="themeClasses.cardTitle" class="card-title">
              <i class="bi bi-bar-chart"></i>
              Reportes de Uso del Sistema
            </h3>
            <div class="card-tools">
              <button type="button" :class="themeClasses.buttonSuccess" class="btn btn-success btn-sm" @click="exportarReporte">
                <i class="bi bi-file-excel"></i>
                Exportar
              </button>
              <button type="button" :class="themeClasses.buttonPrimary" class="btn btn-primary btn-sm ms-2" @click="actualizarDatos">
                <i class="bi bi-arrow-clockwise"></i>
                Actualizar
              </button>
            </div>
          </div>
          <div :class="themeClasses.cardBody" class="card-body">
            <!-- Filtros -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label :class="themeClasses.label" class="form-label">Período</label>
                <select :class="themeClasses.formControl" class="form-select" v-model="filtros.periodo" @change="cargarDatos">
                  <option value="hoy">Hoy</option>
                  <option value="semana">Última Semana</option>
                  <option value="mes">Último Mes</option>
                  <option value="trimestre">Último Trimestre</option>
                  <option value="personalizado">Personalizado</option>
                </select>
              </div>
              <div class="col-md-3" v-if="filtros.periodo === 'personalizado'">
                <label class="form-label">Desde</label>
                <input type="date" class="form-control" v-model="filtros.fecha_desde" @change="cargarDatos">
              </div>
              <div class="col-md-3" v-if="filtros.periodo === 'personalizado'">
                <label class="form-label">Hasta</label>
                <input type="date" class="form-control" v-model="filtros.fecha_hasta" @change="cargarDatos">
              </div>
              <div class="col-md-3">
                <label class="form-label">Empresa</label>
                <select class="form-select" v-model="filtros.empresa_id" @change="cargarDatos">
                  <option value="">Todas las empresas</option>
                  <option v-for="empresa in empresas" :key="empresa.id" :value="empresa.id">
                    {{ empresa.nombre }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Métricas generales -->
            <div class="row mb-4">
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-people fs-1 text-primary"></i>
                    <h4>{{ metricas.usuarios_activos }}</h4>
                    <p class="mb-0">Usuarios Activos</p>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-eye fs-1 text-success"></i>
                    <h4>{{ metricas.sesiones_totales }}</h4>
                    <p class="mb-0">Sesiones</p>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-clock fs-1 text-info"></i>
                    <h4>{{ metricas.tiempo_promedio }}m</h4>
                    <p class="mb-0">Tiempo Promedio</p>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-file-text fs-1 text-warning"></i>
                    <h4>{{ metricas.facturas_generadas }}</h4>
                    <p class="mb-0">Facturas</p>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-hdd fs-1 text-danger"></i>
                    <h4>{{ metricas.almacenamiento_usado }}GB</h4>
                    <p class="mb-0">Almacenamiento</p>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-cloud-arrow-down fs-1 text-secondary"></i>
                    <h4>{{ metricas.api_calls }}</h4>
                    <p class="mb-0">API Calls</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Gráficos -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Actividad de Usuarios (Últimos 30 días)</h5>
                  </div>
                  <div class="card-body">
                    <canvas ref="actividadChart" height="200"></canvas>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Uso por Módulos</h5>
                  </div>
                  <div class="card-body">
                    <canvas ref="modulosChart" height="200"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tablas de detalles -->
            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Top Empresas por Uso</h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                          <tr>
                            <th>Empresa</th>
                            <th>Usuarios</th>
                            <th>Sesiones</th>
                            <th>Facturas</th>
                            <th>Almacenamiento</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="empresa in topEmpresas" :key="empresa.id">
                            <td>
                              <strong>{{ empresa.nombre }}</strong>
                              <br>
                              <small class="text-muted">{{ empresa.plan }}</small>
                            </td>
                            <td>{{ empresa.usuarios_activos }}</td>
                            <td>{{ empresa.sesiones }}</td>
                            <td>{{ empresa.facturas }}</td>
                            <td>{{ empresa.almacenamiento }}GB</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Módulos Más Utilizados</h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                          <tr>
                            <th>Módulo</th>
                            <th>Usos</th>
                            <th>Empresas</th>
                            <th>Tiempo Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="modulo in modulosPopulares" :key="modulo.id">
                            <td>
                              <i :class="modulo.icon"></i>
                              {{ modulo.nombre }}
                            </td>
                            <td>{{ modulo.usos }}</td>
                            <td>{{ modulo.empresas_activas }}</td>
                            <td>{{ modulo.tiempo_total }}h</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Alertas de uso -->
            <div class="row mt-4" v-if="alertas.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5>Alertas de Uso</h5>
                  </div>
                  <div class="card-body">
                    <div class="alert" :class="getAlertClass(alerta.tipo)" v-for="alerta in alertas" :key="alerta.id">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{{ alerta.empresa_nombre }}</strong>
                          <p class="mb-0">{{ alerta.mensaje }}</p>
                        </div>
                        <div>
                          <span class="badge" :class="getAlertBadgeClass(alerta.tipo)">
                            {{ alerta.tipo }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estadísticas de rendimiento -->
            <div class="row mt-4">
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h6>Rendimiento del Sistema</h6>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Tiempo respuesta promedio:</span>
                      <strong>{{ metricas.tiempo_respuesta_promedio }}ms</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Disponibilidad:</span>
                      <strong class="text-success">{{ metricas.disponibilidad }}%</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Errores 5xx:</span>
                      <strong class="text-danger">{{ metricas.errores_servidor }}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h6>Uso de Recursos</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-2">
                      <span>CPU:</span>
                      <div class="progress mt-1">
                        <div class="progress-bar" :style="`width: ${metricas.cpu_uso}%`">
                          {{ metricas.cpu_uso }}%
                        </div>
                      </div>
                    </div>
                    <div class="mb-2">
                      <span>Memoria:</span>
                      <div class="progress mt-1">
                        <div class="progress-bar bg-warning" :style="`width: ${metricas.memoria_uso}%`">
                          {{ metricas.memoria_uso }}%
                        </div>
                      </div>
                    </div>
                    <div>
                      <span>Disco:</span>
                      <div class="progress mt-1">
                        <div class="progress-bar bg-danger" :style="`width: ${metricas.disco_uso}%`">
                          {{ metricas.disco_uso }}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h6>Tendencias</h6>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Nuevos usuarios:</span>
                      <span :class="metricas.variacion_usuarios >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_usuarios >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_usuarios) }}%
                      </span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Actividad general:</span>
                      <span :class="metricas.variacion_actividad >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_actividad >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_actividad) }}%
                      </span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Facturación:</span>
                      <span :class="metricas.variacion_facturacion >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_facturacion >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_facturacion) }}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import api from '@/services/api'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'ReportesUso',
  setup() {
    const metricas = ref({})
    const empresas = ref([])
    const topEmpresas = ref([])
    const modulosPopulares = ref([])
    const alertas = ref([])
    const actividadChart = ref(null)
    const modulosChart = ref(null)
    let chartInstances = []
    
    const filtros = ref({
      periodo: 'mes',
      fecha_desde: '',
      fecha_hasta: '',
      empresa_id: ''
    })

    const cargarDatos = async () => {
      try {
        const params = { ...filtros.value }
        const [
          metricasRes, 
          empresasRes, 
          topEmpresasRes, 
          modulosRes, 
          alertasRes
        ] = await Promise.all([
          api.get('/admin/reportes/uso/metricas', { params }),
          api.get('/admin/empresas'),
          api.get('/admin/reportes/uso/top-empresas', { params }),
          api.get('/admin/reportes/uso/modulos-populares', { params }),
          api.get('/admin/reportes/uso/alertas', { params })
        ])

        metricas.value = metricasRes.data
        empresas.value = (empresasRes.data?.data || []).map(e => ({
          id: e.id,
          nombre: e.razon_social || e.business_name || e.trade_name || e.ruc
        }))
        topEmpresas.value = topEmpresasRes.data
        modulosPopulares.value = modulosRes.data
        alertas.value = alertasRes.data
        
        await nextTick()
        crearGraficos()
      } catch (error) {
        console.error('Error loading datos:', error)
      }
    }

    const crearGraficos = () => {
      // Limpiar gráficos existentes
      chartInstances.forEach(chart => chart.destroy())
      chartInstances = []

      // Gráfico de actividad de usuarios
      if (actividadChart.value && metricas.value.actividad_diaria) {
        const ctx = actividadChart.value.getContext('2d')
        const chart = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: metricas.value.actividad_diaria.map(item => item.fecha),
            datasets: [
              {
                label: 'Usuarios Activos',
                data: metricas.value.actividad_diaria.map(item => item.usuarios_activos),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.1
              },
              {
                label: 'Sesiones',
                data: metricas.value.actividad_diaria.map(item => item.sesiones),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                tension: 0.1
              }
            ]
          },
          options: {
            responsive: true,
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        })
        chartInstances.push(chart)
      }

      // Gráfico de uso por módulos
      if (modulosChart.value && metricas.value.uso_modulos) {
        const ctx = modulosChart.value.getContext('2d')
        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels: metricas.value.uso_modulos.map(item => item.nombre),
            datasets: [{
              label: 'Usos',
              data: metricas.value.uso_modulos.map(item => item.usos),
              backgroundColor: [
                'rgba(255, 99, 132, 0.5)',
                'rgba(54, 162, 235, 0.5)',
                'rgba(255, 205, 86, 0.5)',
                'rgba(75, 192, 192, 0.5)',
                'rgba(153, 102, 255, 0.5)'
              ],
              borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 205, 86, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(153, 102, 255, 1)'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        })
        chartInstances.push(chart)
      }
    }

    const actualizarDatos = () => {
      cargarDatos()
    }

    const exportarReporte = async () => {
      try {
        const response = await api.get('/admin/reportes/uso/exportar', {
          params: filtros.value,
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `reporte_uso_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error exportando reporte:', error)
      }
    }

    const getAlertClass = (tipo) => {
      const classes = {
        limite_excedido: 'alert-danger',
        uso_elevado: 'alert-warning',
        inactividad: 'alert-info',
        rendimiento: 'alert-warning'
      }
      return classes[tipo] || 'alert-info'
    }

    const getAlertBadgeClass = (tipo) => {
      const classes = {
        limite_excedido: 'bg-danger',
        uso_elevado: 'bg-warning',
        inactividad: 'bg-info',
        rendimiento: 'bg-warning'
      }
      return classes[tipo] || 'bg-secondary'
    }

    onMounted(() => {
      cargarDatos()
    })

    return {
      metricas,
      empresas,
      topEmpresas,
      modulosPopulares,
      alertas,
      filtros,
      actividadChart,
      modulosChart,
      cargarDatos,
      actualizarDatos,
      exportarReporte,
      getAlertClass,
      getAlertBadgeClass
    }
  }
}
</script>

<style>
.card {
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

canvas {
  max-height: 400px;
}

.progress {
  height: 20px;
}
</style>
